import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import { Card, Badge } from '../components/Shared';
import { kpis, spendData, vendors, contracts } from '../data/mock';
import SafeIcon from '../common/SafeIcon';
import { FiTrendingUp, FiAlertCircle, FiShield, FiDollarSign, FiArrowUpRight, FiClock } from 'react-icons/fi';

const Dashboard = () => {
  const radarOptions = {
    backgroundColor: 'transparent',
    grid: { top: 40, right: 40, bottom: 40, left: 40 },
    xAxis: {
      type: 'value',
      name: 'STRATEGIC VALUE',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { color: '#475569', fontSize: 10, fontWeight: 'bold', letterSpacing: 1 },
      splitLine: { lineStyle: { color: '#1e293b', type: 'solid', opacity: 0.5 } },
      axisLabel: { show: false },
      axisLine: { lineStyle: { color: '#334155' } }
    },
    yAxis: {
      type: 'value',
      name: 'RISK EXPOSURE',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { color: '#475569', fontSize: 10, fontWeight: 'bold', letterSpacing: 1 },
      splitLine: { lineStyle: { color: '#1e293b', type: 'solid', opacity: 0.5 } },
      axisLabel: { show: false },
      axisLine: { lineStyle: { color: '#334155' } }
    },
    tooltip: {
      backgroundColor: '#0F1626',
      borderColor: '#334155',
      padding: [10, 15],
      textStyle: { color: '#f8fafc', fontSize: 12 },
      formatter: (params) => `
        <div style="font-weight: 800; color: #facc15; margin-bottom: 4px;">${params.data.name}</div>
        <div style="font-size: 10px; color: #94a3b8;">Value Score: ${params.data.value[0]}</div>
        <div style="font-size: 10px; color: #94a3b8;">Risk Index: ${params.data.value[1]}</div>
      `
    },
    series: [{
      type: 'scatter',
      symbolSize: (data) => Math.sqrt(data[2]) * 4.5,
      itemStyle: {
        color: (params) => {
          if (params.data.value[1] > 70) return '#ef4444';
          if (params.data.value[1] > 40) return '#eab308';
          return '#10b981';
        },
        shadowBlur: 20,
        shadowColor: 'rgba(0, 0, 0, 0.4)',
        opacity: 0.8
      },
      emphasis: {
        itemStyle: { opacity: 1, shadowBlur: 30, shadowColor: 'rgba(250, 204, 21, 0.4)' }
      },
      data: vendors.map(v => ({
        name: v.name,
        value: [v.value, v.riskScore, parseInt(v.spend.replace(/\D/g,''))]
      }))
    }]
  };

  const spendOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', backgroundColor: '#0F1626', borderColor: '#334155', textStyle: { color: '#fff' } },
    grid: { left: '4%', right: '4%', bottom: '10%', top: '15%', containLabel: true },
    xAxis: { 
      type: 'category', 
      boundaryGap: false, 
      data: spendData.months, 
      axisLine: { lineStyle: { color: '#334155' } }, 
      axisLabel: { color: '#64748b', fontSize: 10, margin: 15 } 
    },
    yAxis: { 
      type: 'value', 
      splitLine: { lineStyle: { color: '#1e293b', type: 'dashed' } }, 
      axisLabel: { color: '#64748b', formatter: '${value}M', fontSize: 10 } 
    },
    series: [{
      name: 'Managed Spend',
      type: 'line',
      smooth: 0.4,
      symbol: 'circle',
      symbolSize: 8,
      itemStyle: { color: '#facc15' },
      lineStyle: { width: 4, color: '#facc15', shadowBlur: 10, shadowColor: 'rgba(250, 204, 21, 0.3)' },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [{ offset: 0, color: 'rgba(250, 204, 21, 0.2)' }, { offset: 1, color: 'rgba(250, 204, 21, 0)' }]
        }
      },
      data: spendData.values
    }]
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-8 max-w-[1600px] mx-auto space-y-8">
      
      <div className="flex flex-col md:flex-row justify-between items-end gap-4">
        <div>
          <h1 className="text-3xl font-black text-white tracking-tight">Intelligence Hub</h1>
          <p className="text-slate-500 font-medium">Real-time oversight of your global vendor ecosystem.</p>
        </div>
        <div className="flex gap-2">
          <Badge type="default">Last sync: 2 mins ago</Badge>
          <Badge type="safe">API Status: Optimal</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'Managed Spend', value: kpis.totalSpend, sub: kpis.spendTrend, icon: FiDollarSign, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
          { label: 'Active Partners', value: kpis.activeVendors, sub: 'Global footprint', icon: FiShield, color: 'text-blue-400', bg: 'bg-blue-500/10' },
          { label: 'Renewal Queue', value: kpis.expiring30Days, sub: 'Next 30 days', icon: FiClock, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
          { label: 'Risk Alerts', value: kpis.highRisk, sub: 'Immediate action', icon: FiAlertCircle, color: 'text-red-400', bg: 'bg-red-500/10' }
        ].map((kpi, i) => (
          <Card key={i} className="p-6 relative group border-slate-800/50 hover:border-slate-700 transition-all duration-500">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.15em]">{kpi.label}</p>
                <h3 className="text-3xl font-black text-white">{kpi.value}</h3>
                <div className="flex items-center gap-1.5 mt-2">
                  <span className={`text-xs font-bold ${kpi.color}`}>{kpi.sub}</span>
                  <SafeIcon icon={FiArrowUpRight} className={`text-xs ${kpi.color}`} />
                </div>
              </div>
              <div className={`p-3 rounded-xl border border-white/5 ${kpi.bg}`}>
                <SafeIcon icon={kpi.icon} className={`text-xl ${kpi.color}`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <Card className="p-8 lg:col-span-8 overflow-hidden">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
            <div>
              <h2 className="text-xl font-black text-white tracking-tight">Supplier Strategic Radar</h2>
              <p className="text-sm text-slate-500 font-medium">Correlation between business impact and operational risk.</p>
            </div>
            <div className="flex bg-[#0A0F1C] border border-slate-800 p-1 rounded-lg">
              <button className="px-3 py-1.5 text-[10px] font-bold text-yellow-400 bg-yellow-400/10 rounded-md">SCATTER</button>
              <button className="px-3 py-1.5 text-[10px] font-bold text-slate-500 hover:text-slate-300">HEATMAP</button>
            </div>
          </div>
          <div className="h-[400px] w-full">
            <ReactECharts option={radarOptions} style={{ height: '100%', width: '100%' }} />
          </div>
        </Card>

        <Card className="p-6 lg:col-span-4 flex flex-col h-full bg-gradient-to-b from-[#0F1626] to-[#0A0F1C]">
          <div className="mb-6">
            <h2 className="text-xl font-black text-white tracking-tight">Urgent Actions</h2>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-widest mt-1">Priority Renewals</p>
          </div>
          
          <div className="flex-1 space-y-3 overflow-y-auto pr-1 custom-scrollbar">
            {contracts.filter(c => c.expiry <= 30).map(c => (
              <div key={c.id} className="group p-4 bg-[#0A0F1C]/50 border border-slate-800 rounded-xl hover:border-yellow-400/40 transition-all cursor-pointer">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-bold text-slate-200 text-sm group-hover:text-yellow-400 transition-colors truncate">{c.vendor}</h4>
                  <Badge type={c.status}>{c.expiry}d</Badge>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[10px] text-slate-500 font-mono">{c.type}</span>
                  <span className="text-xs font-black text-white">{c.value}</span>
                </div>
              </div>
            ))}
          </div>
          
          <button className="mt-6 w-full py-3 bg-slate-800/50 hover:bg-slate-800 text-slate-300 text-xs font-bold rounded-xl border border-slate-700 transition-all">
            VIEW ALL CONTRACTS
          </button>
        </Card>
      </div>

      <Card className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-xl font-black text-white tracking-tight">Financial Trajectory</h2>
            <p className="text-sm text-slate-500 font-medium">Consolidated spend across all business units.</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Projected Year End</p>
            <p className="text-xl font-black text-emerald-400">$18.5M</p>
          </div>
        </div>
        <div className="h-80 w-full">
          <ReactECharts option={spendOptions} style={{ height: '100%', width: '100%' }} />
        </div>
      </Card>
    </motion.div>
  );
};

export default Dashboard;